/**
 * 🟥 CUADRO NEGRO V5 — DiffViewer
 *
 * V9.7 — Side-by-side con motor jsdiff (`diffLines`), bloques alineados
 * added/removed/context/modified, scroll sincronizado entre paneles, layout
 * responsive (stack vertical en mobile) y empty state "Sin diferencias".
 * El modo unified sigue usando el unified_diff del backend (sin cambios).
 */
import { useState, useRef, useMemo, useCallback } from 'react';
import { diffLines } from 'diff';
import { Columns, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// 🟦 V9.7 — Construye pares left/right alineados a partir de jsdiff blocks
function buildSidePairs(leftStr, rightStr) {
  const blocks = diffLines(leftStr || '', rightStr || '');
  const pairs = [];
  let i = 0;
  while (i < blocks.length) {
    const b = blocks[i];
    const lines = b.value.split('\n');
    if (lines.length && lines[lines.length - 1] === '') lines.pop();
    if (!b.added && !b.removed) {
      for (const ln of lines) pairs.push({ left: ln, right: ln, type: 'context' });
      i++;
    } else if (b.removed && blocks[i + 1] && blocks[i + 1].added) {
      const addedLines = blocks[i + 1].value.split('\n');
      if (addedLines.length && addedLines[addedLines.length - 1] === '') addedLines.pop();
      const max = Math.max(lines.length, addedLines.length);
      for (let k = 0; k < max; k++) {
        pairs.push({
          left: lines[k] !== undefined ? lines[k] : null,
          right: addedLines[k] !== undefined ? addedLines[k] : null,
          type: 'modified',
        });
      }
      i += 2;
    } else if (b.removed) {
      for (const ln of lines) pairs.push({ left: ln, right: null, type: 'removed' });
      i++;
    } else if (b.added) {
      for (const ln of lines) pairs.push({ left: null, right: ln, type: 'added' });
      i++;
    } else {
      i++;
    }
  }
  return pairs;
}

export const DiffViewer = ({ file, mode = 'side', onModeChange }) => {
  const { language } = useLanguage();
  const isEs = (language || 'es').toLowerCase().startsWith('es');
  const [internalMode, setInternalMode] = useState(mode);
  const leftScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const syncingRef = useRef(false);

  // 🟦 V9.7 — sincroniza scroll vertical entre paneles
  const onLeftScroll = useCallback(() => {
    if (syncingRef.current) { syncingRef.current = false; return; }
    if (rightScrollRef.current && leftScrollRef.current) {
      syncingRef.current = true;
      rightScrollRef.current.scrollTop = leftScrollRef.current.scrollTop;
    }
  }, []);
  const onRightScroll = useCallback(() => {
    if (syncingRef.current) { syncingRef.current = false; return; }
    if (leftScrollRef.current && rightScrollRef.current) {
      syncingRef.current = true;
      leftScrollRef.current.scrollTop = rightScrollRef.current.scrollTop;
    }
  }, []);

  // 🟦 V9.7 — pairs computados (memo)
  const pairs = useMemo(() => {
    if (!file) return [];
    return buildSidePairs(file.left || '', file.right || '');
  }, [file]);
  const hasChanges = useMemo(() => pairs.some(p => p.type !== 'context'), [pairs]);

  if (!file) {
    return (
      <div className="p-6 text-slate-400 text-sm">
        {isEs ? 'Selecciona un archivo para ver el diff.' : 'Select a file to view diff.'}
      </div>
    );
  }

  const currentMode = onModeChange ? mode : internalMode;
  const setMode = (m) => {
    setInternalMode(m);
    onModeChange?.(m);
  };

  const unifiedLines = (file.unified_diff || '').split('\n');

  const renderLine = (text, type, side) => {
    if (text === null) {
      return <div className="px-2 py-px bg-slate-900/20 select-none">&nbsp;</div>;
    }
    const cls =
      type === 'added' ? 'bg-green-500/20 border-l-2 border-emerald-500 text-emerald-100' :
      type === 'removed' ? 'bg-red-500/20 border-l-2 border-rose-500 text-rose-100' :
      type === 'modified'
        ? (side === 'left' ? 'bg-red-500/15 border-l-2 border-rose-500/70 text-rose-100' : 'bg-green-500/15 border-l-2 border-emerald-500/70 text-emerald-100')
        : 'bg-slate-800/40 text-slate-300';
    const testid = type === 'added' ? 'diff-line-added' : type === 'removed' ? 'diff-line-removed' : type === 'modified' ? `diff-line-${side === 'left' ? 'removed' : 'added'}` : 'diff-line-context';
    return (
      <div className={`px-2 py-px whitespace-pre ${cls}`} data-testid={testid}>
        {text || '\u00A0'}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-slate-950 text-slate-200" data-testid="diff-viewer">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800">
        <div className="text-xs text-slate-300 font-mono truncate">
          <span className={`mr-2 inline-block px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold ${
            file.status === 'added' ? 'bg-emerald-500/20 text-emerald-300' :
            file.status === 'removed' ? 'bg-rose-500/20 text-rose-300' :
            file.status === 'modified' ? 'bg-amber-500/20 text-amber-300' :
            'bg-slate-700/40 text-slate-400'
          }`}>{file.status}</span>
          {file.path}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMode('side')}
            className={`px-2 py-1 text-xs rounded ${currentMode === 'side' ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-slate-200'}`}
            data-testid="diff-mode-side"
          >
            <Columns className="w-3.5 h-3.5 inline mr-1" />
            {isEs ? 'Lado a lado' : 'Side-by-side'}
          </button>
          <button
            type="button"
            onClick={() => setMode('unified')}
            className={`px-2 py-1 text-xs rounded ${currentMode === 'unified' ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-slate-200'}`}
            data-testid="diff-mode-unified"
          >
            <FileText className="w-3.5 h-3.5 inline mr-1" />
            {isEs ? 'Unificado' : 'Unified'}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden" data-testid="diff-viewer-container">
        {currentMode === 'side' ? (
          !hasChanges ? (
            <div className="p-6 text-slate-400 text-sm italic" data-testid="diff-empty">
              {isEs ? 'Sin diferencias' : 'No differences'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 h-full" data-testid="diff-scroll-sync">
              <div
                ref={leftScrollRef}
                onScroll={onLeftScroll}
                className="border-b md:border-b-0 md:border-r border-slate-800 overflow-auto"
              >
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/60 sticky top-0 z-10">
                  {isEs ? 'Izquierda' : 'Left'}
                </div>
                <div className="text-xs leading-relaxed font-mono">
                  {pairs.map((p, idx) => (
                    <div key={`L-${idx}`}>{renderLine(p.left, p.type, 'left')}</div>
                  ))}
                </div>
              </div>
              <div
                ref={rightScrollRef}
                onScroll={onRightScroll}
                className="overflow-auto"
              >
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/60 sticky top-0 z-10">
                  {isEs ? 'Derecha' : 'Right'}
                </div>
                <div className="text-xs leading-relaxed font-mono">
                  {pairs.map((p, idx) => (
                    <div key={`R-${idx}`}>{renderLine(p.right, p.type, 'right')}</div>
                  ))}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="h-full overflow-auto">
            <pre className="m-0 p-3 text-xs leading-relaxed whitespace-pre font-mono">
              {unifiedLines.map((line, idx) => {
                const color =
                  line.startsWith('+++') || line.startsWith('---') ? 'text-slate-500' :
                  line.startsWith('@@') ? 'text-cyan-400' :
                  line.startsWith('+') ? 'text-emerald-300 bg-emerald-500/10' :
                  line.startsWith('-') ? 'text-rose-300 bg-rose-500/10' :
                  'text-slate-300';
                return (
                  <div key={idx} className={`px-1 ${color}`}>
                    {line || '\u00A0'}
                  </div>
                );
              })}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiffViewer;
